import CallIcon from "@mui/icons-material/Call";
import { Drawer, IconButton } from "@mui/material";
import { useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  closeIncomingCalls,
  openIncomingCalls,
} from "../../../store/slices/incomingCalls";

import CallSidebar from "./callSidebar";
import { incomingCallDrawerSx } from "./overlayPolicy";

import type { AppDispatch, RootState } from "../../../store/store";

/**
 * Button for the AppBar toolbar — dispatches openIncomingCalls on click.
 * The Drawer is rendered globally in MinimalLayout so it works on every route.
 */
const IncomingCalls = () => {
  const dispatch = useDispatch<AppDispatch>();

  const handleOpen = () => {
    dispatch(openIncomingCalls());
  };

  return (
    <IconButton color="inherit" title="Incoming Calls" onClick={handleOpen}>
      <CallIcon />
    </IconButton>
  );
};

/**
 * Global Drawer — mount once at the layout level so it can open on any route.
 */
export const IncomingCallsDrawer = () => {
  const { isOpen } = useSelector((state: RootState) => state.incomingCalls);
  const dispatch = useDispatch<AppDispatch>();
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const restoreFocusFrameRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      return () => {
        previouslyFocusedElementRef.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
      };
    }

    if (restoreFocusFrameRef.current !== null) {
      window.cancelAnimationFrame(restoreFocusFrameRef.current);
      restoreFocusFrameRef.current = null;
    }

    return () => {
      const previouslyFocusedElement = previouslyFocusedElementRef.current;
      restoreFocusFrameRef.current = window.requestAnimationFrame(() => {
        if (previouslyFocusedElement?.isConnected) {
          previouslyFocusedElement.focus({ preventScroll: true });
        }
        restoreFocusFrameRef.current = null;
      });
    };
  }, [isOpen]);

  useLayoutEffect(
    () => () => {
      if (restoreFocusFrameRef.current !== null) {
        window.cancelAnimationFrame(restoreFocusFrameRef.current);
      }
    },
    []
  );

  const handleClose = () => {
    dispatch(closeIncomingCalls());
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        "aria-label": "Incoming calls",
        tabIndex: -1,
      }}
      sx={incomingCallDrawerSx}
    >
      <CallSidebar />
    </Drawer>
  );
};

export default IncomingCalls;
